/* eslint-disable react/jsx-max-props-per-line */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Box,
  ButtonBase,
  Chip,
  IconButton,
  InputAdornment,
  Link,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { EmptyState, InputV2 } from '../../../../../components/v2';
import DigitacaoTab from './DigitacaoTab';
import PrioridadeProcedimentoChip from './PrioridadeProcedimentoChip';
import { idsGerais, idsRegiaoCompleta, idsServicoDaNsmp } from '../hooks/useMtBtProcedimentos';

const STORAGE_KEY = 'ep-apoio-digitacao-recolhido';
/** `nsmp` = o que vale para esta NSMP (serviços + gerais); `regiao` = tudo o que está cadastrado. */
const FILTROS_VISTA = [
  { vista: 'nsmp', rotulo: 'Específica' },
  { vista: 'regiao', rotulo: 'Todas' },
];
const STORAGE_LARGURA = 'ep-apoio-digitacao-largura';
const LARGURA_PADRAO = 360;
const LARGURA_MIN = 300;
const LARGURA_MAX = 720;
/** Passo do teclado no divisor (Shift multiplica). */
const PASSO_LARGURA = 16;

/** Nunca deixa o painel tomar mais de 60% da janela, nem sair da faixa permitida. */
const limitarLargura = (valor) => {
  const teto = Math.min(LARGURA_MAX, Math.round(window.innerWidth * 0.6));
  return Math.min(Math.max(valor, LARGURA_MIN), Math.max(teto, LARGURA_MIN));
};

const lerLargura = () => {
  try {
    const salva = Number(window.localStorage.getItem(STORAGE_LARGURA));
    return salva ? limitarLargura(salva) : LARGURA_PADRAO;
  } catch {
    return LARGURA_PADRAO;
  }
};

const gravarLargura = (largura) => {
  try {
    window.localStorage.setItem(STORAGE_LARGURA, String(largura));
  } catch {
    // a largura vale na sessão mesmo sem persistir
  }
};

const DIVISOR_SX = {
  display: { xs: 'none', md: 'block' },
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: -5,
  width: 10,
  zIndex: 1,
  cursor: 'col-resize',
  touchAction: 'none',
  outline: 'none',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 4,
    width: 2,
    borderRadius: 1,
    bgcolor: 'transparent',
  },
  '&:hover::after, &:focus-visible::after, &:active::after': { bgcolor: 'primary.main' },
};

const TRILHA_BOTAO_SX = {
  flexDirection: 'column',
  gap: 1,
  width: 38,
  py: 1.25,
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  bgcolor: 'action.hover',
  '&:hover': { bgcolor: 'action.selected' },
};

const TRILHA_TOTAL_SX = {
  minWidth: 20,
  height: 20,
  px: 0.5,
  borderRadius: 10,
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  fontSize: 11,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontVariantNumeric: 'tabular-nums',
};

const ABAS_SX = {
  minHeight: 40,
  mb: 1.5,
  borderBottom: 1,
  borderColor: 'divider',
  '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontSize: 13, fontWeight: 600 },
};
/** A busca só aparece quando a vista tem lista para varrer. */
const MINIMO_PARA_BUSCA = 6;
/** Etiquetas de tipo de serviço visíveis no card recolhido; o resto vai para "+N". */
const MAXIMO_ETIQUETAS = 2;

const lerRecolhido = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const gravarRecolhido = (recolhido) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, recolhido ? '1' : '0');
  } catch {
    // a coluna funciona sem persistir
  }
};

const semAcento = (texto) => String(texto || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase();

const textoPlano = (html) => String(html || '').replace(/<[^>]*>/g, ' ');

const corresponde = ({ procedimento, etiquetas }, termo) => {
  if (!termo) return true;
  const base = [
    procedimento.titulo,
    procedimento.escopo?.nome,
    ...etiquetas,
    textoPlano(procedimento.conteudoHtml),
  ].join(' ');
  return semAcento(base).includes(termo);
};

const etiquetaEscopo = (procedimento) => {
  const escopo = procedimento?.escopo;
  if (!escopo?.nome) return 'Todas as regiões';
  return escopo.nome;
};

/** Texto semântico legível nos dois modos (o `.dark` some no fundo escuro). */
const corSemantica = (cor) => (theme) => (
  theme.palette.mode === 'dark' ? theme.palette[cor].light : theme.palette[cor].dark
);

const TITULO_RECOLHIDO_SX = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const CONTEUDO_SX = {
  fontSize: 13,
  lineHeight: 1.5,
  overflowWrap: 'anywhere',
  '& > :first-child': { mt: 0 },
  '& > :last-child': { mb: 0 },
  '& p': { my: 0.75 },
  '& ul, & ol': { my: 0.75, pl: 2.5 },
  '& li': { my: 0.25 },
  '& li > p': { my: 0 },
  '& strong, & b': { fontWeight: 600 },
  '& h3, & h4': { fontSize: 14, fontWeight: 600, lineHeight: 1.3, my: 1 },
  '& blockquote': {
    m: 0,
    my: 0.75,
    pl: 1.5,
    borderLeft: 3,
    borderColor: 'divider',
    color: 'text.secondary',
  },
  '& mark': {
    bgcolor: (theme) => alpha(theme.palette.warning.main, 0.3),
    color: 'inherit',
    px: 0.25,
  },
  '& a': { color: corSemantica('primary'), textDecoration: 'underline' },
  '& table': {
    display: 'block',
    maxWidth: '100%',
    overflowX: 'auto',
    borderCollapse: 'collapse',
  },
  '& th, & td': { border: 1, borderColor: 'divider', px: 0.75, py: 0.5, fontSize: 12 },
};

const ProcedimentoCard = ({ id, procedimento, etiquetas, aberto, onToggle }) => {
  const anexos = procedimento.anexos || [];
  const corpoId = `apoio-procedimento-${id}`;
  return (
    <Box
      id={`apoio-card-${id}`}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <ButtonBase
        component="div"
        onClick={onToggle}
        aria-expanded={aberto}
        aria-controls={corpoId}
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          p: 1.25,
          textAlign: 'left',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {etiquetas.length > 0 && (
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mb: 0.75 }}>
              {etiquetas.slice(0, MAXIMO_ETIQUETAS).map((etiqueta) => (
                <Chip key={etiqueta} size="small" color="primary" variant="outlined" label={etiqueta} sx={{ maxWidth: '100%' }} />
              ))}
              {etiquetas.length > MAXIMO_ETIQUETAS && (
                <Tooltip title={etiquetas.slice(MAXIMO_ETIQUETAS).join(', ')}>
                  <Chip size="small" variant="outlined" label={`+${etiquetas.length - MAXIMO_ETIQUETAS}`} />
                </Tooltip>
              )}
            </Stack>
          )}
          <Typography variant="subtitle2" sx={aberto ? undefined : TITULO_RECOLHIDO_SX}>
            {procedimento.titulo}
          </Typography>
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 0.75 }}>
            <Chip size="small" label={etiquetaEscopo(procedimento)} />
            <PrioridadeProcedimentoChip prioridade={procedimento.prioridade} />
            {anexos.length > 0 && (
              <Stack direction="row" alignItems="center" spacing={0.25} sx={{ color: 'text.secondary' }}>
                <AttachFileIcon sx={{ fontSize: 14 }} />
                <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {anexos.length}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            flexShrink: 0,
            color: 'text.secondary',
            transform: aberto ? 'rotate(180deg)' : 'none',
          }}
        />
      </ButtonBase>
      {aberto && (
        <Box
          id={corpoId}
          sx={{ px: 1.25, pb: 1.25, pt: 1, borderTop: 1, borderColor: 'divider' }}
        >
          {procedimento.conteudoHtml ? (
            <Box
              sx={CONTEUDO_SX}
              dangerouslySetInnerHTML={{ __html: procedimento.conteudoHtml }}
            />
          ) : (
            <Typography variant="caption" color="text.secondary">Sem texto.</Typography>
          )}
          {anexos.length > 0 && (
            <Stack spacing={0.25} sx={{ mt: 1 }}>
              {anexos.map((anexo) => (
                <Link
                  key={anexo.id}
                  href={anexo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                >
                  {anexo.nome}{anexo.extensao ? `.${anexo.extensao}` : ''}
                </Link>
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};

ProcedimentoCard.propTypes = {
  id: PropTypes.string.isRequired,
  procedimento: PropTypes.object.isRequired,
  etiquetas: PropTypes.array,
  aberto: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

ProcedimentoCard.defaultProps = {
  etiquetas: [],
  aberto: false,
};

const RANK_IMPORTANCIA = { ALTA: 0, MEDIA: 1, BAIXA: 2 };

/** Alta, Média, Baixa; empate mantém a ordem que veio do servidor (ordenação estável). */
const porImportancia = (a, b) =>
  (RANK_IMPORTANCIA[a.procedimento.prioridade] ?? 3) - (RANK_IMPORTANCIA[b.procedimento.prioridade] ?? 3);

/** Tipos de serviço e categorias que disparam o procedimento. Procedimento geral não tem etiqueta. */
const etiquetasDo = (procedimento, servicoIds) => {
  const nomes = [];
  (procedimento.gatilhos?.tiposServico || []).forEach((tipo) => {
    if (!servicoIds.length || servicoIds.includes(tipo.id)) nomes.push(tipo.nome);
  });
  (procedimento.gatilhos?.categorias || []).forEach((categoria) => {
    if (categoria.descricao) nomes.push(categoria.descricao);
  });
  return nomes;
};

const ApoioDigitacaoPanel = ({
  data,
  loading,
  nsmpId,
  nsmpRow,
  readOnly,
  participacaoMode,
  podeForcarEnvio,
  userId,
  onReprocessDone,
  onEnviado,
  digitacaoKey,
  foco,
}) => {
  const [recolhido, setRecolhido] = useState(lerRecolhido);
  const [aba, setAba] = useState('procedimentos');
  const [vista, setVista] = useState('nsmp');
  const [busca, setBusca] = useState('');
  const [abertos, setAbertos] = useState({});
  const abrirAposTrocaDeVista = useRef(null);
  const [largura, setLargura] = useState(lerLargura);
  const arrasto = useRef(null);

  const erroIntegracao = nsmpRow?.resumoIntegracao?.status === 'ERROR_SYNC';
  const totalEventos = nsmpRow?.totalEventos;
  const semProcedimento = !loading && !Object.keys(data?.procedimentos || {}).length;

  // Aba inicial decidida uma vez por NSMP, quando os procedimentos terminam de carregar.
  // Mudanças posteriores de status (reprocesso, envio) não devem tirar o usuário da aba em que está.
  const abaInicialDe = useRef(null);
  useEffect(() => {
    setVista('nsmp');
    abaInicialDe.current = null;
  }, [nsmpId]);

  useEffect(() => {
    if (loading || abaInicialDe.current === nsmpId) return;
    abaInicialDe.current = nsmpId;
    setAba(erroIntegracao || semProcedimento ? 'digitacao' : 'procedimentos');
  }, [nsmpId, loading, erroIntegracao, semProcedimento]);

  useEffect(() => {
    setBusca('');
  }, [nsmpId]);

  useEffect(() => {
    setAbertos(abrirAposTrocaDeVista.current ? { [abrirAposTrocaDeVista.current]: true } : {});
    abrirAposTrocaDeVista.current = null;
  }, [nsmpId, vista]);

  // Pedido externo (ex.: clique no procedimento de um serviço): abre o painel já no card.
  useEffect(() => {
    if (!foco?.id) return undefined;
    const key = `s-${foco.id}`;
    abrirAposTrocaDeVista.current = vista === 'nsmp' ? null : key;
    setRecolhido(false);
    setAba('procedimentos');
    setVista('nsmp');
    setBusca('');
    setAbertos((prev) => ({ ...prev, [key]: true }));
    const timer = window.setTimeout(() => {
      document.getElementById(`apoio-card-${key}`)?.scrollIntoView({ block: 'nearest' });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [foco?.n]);

  const alternar = () => {
    setRecolhido((atual) => {
      gravarRecolhido(!atual);
      return !atual;
    });
  };

  // Divisor arrastável na borda esquerda: puxar para a esquerda alarga, para a direita estreita.
  const iniciarArrasto = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    arrasto.current = { x: event.clientX, largura };
  };

  const arrastar = (event) => {
    if (!arrasto.current) return;
    setLargura(limitarLargura(arrasto.current.largura + arrasto.current.x - event.clientX));
  };

  const soltarArrasto = (event) => {
    if (!arrasto.current) return;
    arrasto.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    gravarLargura(largura);
  };

  const redimensionarPorTeclado = (event) => {
    const direcao = { ArrowLeft: 1, ArrowRight: -1 }[event.key];
    if (!direcao) return;
    event.preventDefault();
    const proxima = limitarLargura(largura + direcao * PASSO_LARGURA * (event.shiftKey ? 3 : 1));
    setLargura(proxima);
    gravarLargura(proxima);
  };

  const restaurarLargura = () => {
    setLargura(LARGURA_PADRAO);
    gravarLargura(LARGURA_PADRAO);
  };

  const abrir = (abaAlvo) => {
    setAba(abaAlvo);
    setRecolhido(false);
    gravarRecolhido(false);
  };

  const regiao = data?.regiao;
  const servicoIds = data?.nsmps?.[nsmpId]?.tipoServicoIds || [];
  const idsDosGerais = idsGerais(data);

  const itensDe = (ids, prefixo, servicos, ehGeral) => ids
    .filter((id) => data?.procedimentos?.[id])
    .map((id) => ({
      key: `${prefixo}-${id}`,
      id,
      geral: ehGeral(id),
      procedimento: data.procedimentos[id],
      etiquetas: etiquetasDo(data.procedimentos[id], servicos),
    }));

  // Específico de serviço vem sempre antes do geral; a importância só desempata dentro do grupo.
  const itensServico = itensDe(idsServicoDaNsmp(data, nsmpId), 's', servicoIds, () => false).sort(porImportancia);
  const itensGerais = itensDe(idsDosGerais, 'g', [], () => true).sort(porImportancia);
  const itensRegiao = itensDe(idsRegiaoCompleta(data), 'r', [], (id) => idsDosGerais.includes(id))
    .sort((a, b) => Number(a.geral) - Number(b.geral) || porImportancia(a, b));

  const termo = semAcento(busca.trim());
  const servicoVisiveis = itensServico.filter((item) => corresponde(item, termo));
  const geraisVisiveis = itensGerais.filter((item) => corresponde(item, termo));
  const regiaoVisiveis = itensRegiao.filter((item) => corresponde(item, termo));

  const contagemNsmp = itensServico.length + itensGerais.length;
  const contagemRegiao = itensRegiao.length;
  const totalDaVista = vista === 'nsmp' ? contagemNsmp : contagemRegiao;
  // Os cards sempre abrem recolhidos: o digitador varre os títulos e abre só o que precisa.
  const estaAberto = (key) => Boolean(abertos[key]);

  const alternarCard = (key) => setAbertos((prev) => ({ ...prev, [key]: !prev[key] }));

  // Polo › gerência › malha: é a chave que decide quais procedimentos valem para esta NSMP.
  const regiaoTexto = [regiao?.polo, regiao?.gerencia, regiao?.malha].filter(Boolean).join(' › ');

  const renderCard = (item) => (
    <ProcedimentoCard
      key={item.key}
      id={item.key}
      procedimento={item.procedimento}
      etiquetas={item.etiquetas}
      aberto={estaAberto(item.key)}
      onToggle={() => alternarCard(item.key)}
    />
  );

  const renderVazio = (mensagemSemBusca) => (
    <EmptyState
      size="compact"
      title={termo ? 'Nada encontrado' : 'Nenhum procedimento'}
      message={termo ? `Nenhum procedimento para “${busca.trim()}”.` : mensagemSemBusca}
    />
  );

  // Recolhido, o painel segue oferecendo as duas abas: cada botão abre já na sua.
  const trilha = [
    {
      aba: 'procedimentos',
      rotulo: 'Documentos',
      dica: `Abrir documentos de apoio${contagemNsmp ? `, ${contagemNsmp} para esta NSMP` : ''}`,
      icone: <MenuBookOutlinedIcon fontSize="small" />,
      total: loading ? 0 : contagemNsmp,
      erro: false,
    },
    {
      aba: 'digitacao',
      rotulo: 'Eventos de digitação',
      dica: `Abrir os eventos de digitação${erroIntegracao ? ', com erro de integração' : ''}`,
      icone: <FactCheckOutlinedIcon fontSize="small" />,
      total: Number(totalEventos) || 0,
      erro: erroIntegracao,
    },
  ];

  if (recolhido) {
    return (
      <Box
        sx={{
          width: 52,
          display: 'flex',
          justifyContent: 'center',
          pt: 1,
          borderLeft: { md: 1 },
          borderColor: 'divider',
          position: { md: 'sticky' },
          top: 0,
          alignSelf: 'flex-start',
        }}
      >
        <Stack spacing={1} alignItems="center">
          {trilha.map((botao) => (
            <Tooltip key={botao.aba} title={botao.dica} placement="left">
              <ButtonBase
                onClick={() => abrir(botao.aba)}
                aria-label={botao.dica}
                sx={TRILHA_BOTAO_SX}
              >
                <ChevronLeftIcon fontSize="small" />
                <Typography
                  variant="caption"
                  sx={{ writingMode: 'vertical-rl', fontWeight: 600, letterSpacing: 0.3 }}
                >
                  {botao.rotulo}
                </Typography>
                <Badge color="error" variant="dot" invisible={!botao.erro}>
                  {botao.icone}
                </Badge>
                {botao.total > 0 && <Box sx={TRILHA_TOTAL_SX}>{botao.total}</Box>}
              </ButtonBase>
            </Tooltip>
          ))}
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: { xs: '100%', md: largura },
        maxHeight: { md: 'calc(100vh - 140px)' },
        display: 'flex',
        flexDirection: 'column',
        position: { md: 'sticky' },
        top: 0,
        borderLeft: { md: 1 },
        borderColor: 'divider',
        pl: { md: 1.5 },
        pt: 1,
      }}
    >
      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label="Redimensionar o painel de apoio"
        aria-valuemin={LARGURA_MIN}
        aria-valuemax={LARGURA_MAX}
        aria-valuenow={largura}
        tabIndex={0}
        onPointerDown={iniciarArrasto}
        onPointerMove={arrastar}
        onPointerUp={soltarArrasto}
        onPointerCancel={soltarArrasto}
        onKeyDown={redimensionarPorTeclado}
        onDoubleClick={restaurarLargura}
        sx={DIVISOR_SX}
      />
      <Box sx={{ flex: '0 0 auto' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Apoio</Typography>
          <IconButton size="small" onClick={alternar} aria-label="Recolher apoio da digitação">
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Tabs
          value={aba}
          variant="fullWidth"
          onChange={(_event, valor) => setAba(valor)}
          sx={ABAS_SX}
        >
          <Tab value="procedimentos" label="Documentos" />
          <Tab
            value="digitacao"
            label={(
              <Badge
                color="error"
                variant="dot"
                invisible={!erroIntegracao}
                sx={{ '& .MuiBadge-badge': { top: 2, right: -8 } }}
              >
                {totalEventos ? `Eventos de digitação ${totalEventos}` : 'Eventos de digitação'}
              </Badge>
            )}
          />
        </Tabs>
        {aba === 'procedimentos' && (
          <Stack spacing={1} sx={{ mb: 1.25 }}>
            {regiaoTexto && (
              <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                Região: {regiaoTexto}
              </Typography>
            )}
            {totalDaVista > MINIMO_PARA_BUSCA && (
              <InputV2
                showInputGroup={false}
                name="busca-procedimento"
                placeholder="Buscar procedimento"
                value={busca}
                onChange={(event) => setBusca(event.target.value)}
                inputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: busca ? (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Limpar busca" onClick={() => setBusca('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            )}
            <Stack direction="row" spacing={0.75} role="group" aria-label="Quais procedimentos mostrar">
              {FILTROS_VISTA.map((filtro) => {
                const selecionado = vista === filtro.vista;
                return (
                  <Chip
                    key={filtro.vista}
                    size="small"
                    clickable
                    aria-pressed={selecionado}
                    color={selecionado ? 'primary' : 'default'}
                    variant={selecionado ? 'filled' : 'outlined'}
                    label={`${filtro.rotulo} (${filtro.vista === 'nsmp' ? contagemNsmp : contagemRegiao})`}
                    onClick={() => setVista(filtro.vista)}
                    sx={{ '& .MuiChip-label': { fontVariantNumeric: 'tabular-nums' } }}
                  />
                );
              })}
            </Stack>
          </Stack>
        )}
      </Box>
      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', pb: 1, pr: 0.5 }}>
        {aba === 'digitacao' ? (
          <DigitacaoTab
            key={digitacaoKey}
            nsmpId={nsmpId}
            nsmpRow={nsmpRow}
            readOnly={readOnly}
            participacaoMode={participacaoMode}
            podeForcarEnvio={podeForcarEnvio}
            userId={userId}
            enabled
            onReprocessDone={onReprocessDone}
            onEnviado={onEnviado}
          />
        ) : (
          <Stack spacing={1.25}>
            {loading && (
              <Stack spacing={1}>
                <Skeleton variant="rounded" height={28} />
                <Skeleton variant="rounded" height={96} />
              </Stack>
            )}
            {!loading && vista === 'nsmp' && (
              <>
                {servicoVisiveis.map(renderCard)}
                {servicoVisiveis.length > 0 && geraisVisiveis.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Gerais da região · {geraisVisiveis.length}
                  </Typography>
                )}
                {geraisVisiveis.map(renderCard)}
                {!servicoVisiveis.length && !geraisVisiveis.length
                  && renderVazio('Esta NSMP não tem procedimento de serviço nem geral da região.')}
              </>
            )}
            {!loading && vista === 'regiao' && (
              regiaoVisiveis.length
                ? regiaoVisiveis.map(renderCard)
                : renderVazio('Não há procedimento aplicável a esta região.')
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

ApoioDigitacaoPanel.propTypes = {
  data: PropTypes.object,
  loading: PropTypes.bool,
  nsmpId: PropTypes.string,
  nsmpRow: PropTypes.object,
  readOnly: PropTypes.bool,
  participacaoMode: PropTypes.string,
  podeForcarEnvio: PropTypes.bool,
  userId: PropTypes.string,
  onReprocessDone: PropTypes.func,
  onEnviado: PropTypes.func,
  digitacaoKey: PropTypes.number,
  foco: PropTypes.shape({ id: PropTypes.string, n: PropTypes.number }),
};

ApoioDigitacaoPanel.defaultProps = {
  data: null,
  loading: false,
  nsmpId: null,
  nsmpRow: null,
  readOnly: false,
  participacaoMode: 'ATIVO',
  podeForcarEnvio: false,
  userId: null,
  onReprocessDone: undefined,
  onEnviado: undefined,
  digitacaoKey: 0,
  foco: null,
};

export default ApoioDigitacaoPanel;
